import React from 'react';
import DateChips from './date-chips';
import TimeChips from './time-chips';
import OtherDateModal from './other-date-modal';
import { TimezoneLabel } from './chip';
import { generateAvailableDates, generateTimeSlots, isSameDay } from './helpers';

const DateTimeSlotPicker = ({
  selected,
  onChange,
  filterDate,
  filterTime,
  timeIntervals = 60,
  minDate,
  maxDaysOut = 14,
  timezoneLabel
}) => {
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [selectedTime, setSelectedTime] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);

  // Sync local state from the selected prop
  React.useEffect(() => {
    if (selected) {
      const d = new Date(selected);
      setSelectedDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      setSelectedTime(d);
    }
  }, [selected]);

  const availableDates = React.useMemo(() => {
    return generateAvailableDates(filterDate, minDate, maxDaysOut, filterTime, timeIntervals);
  }, [filterDate, filterTime, minDate, maxDaysOut, timeIntervals]);

  // Auto-select first available date when no selection is provided
  React.useEffect(() => {
    if (!selected && availableDates.length > 0) {
      setSelectedDate(availableDates[0]);
    }
  }, []);

  const visibleDates = React.useMemo(() => {
    const dates = [...availableDates];
    // If the selected date is outside the visible range, append it
    if (selectedDate && !dates.some((d) => isSameDay(d, selectedDate))) {
      dates.push(selectedDate);
    }
    return dates;
  }, [availableDates, selectedDate]);

  const timeSlots = React.useMemo(
    () => (selectedDate ? generateTimeSlots(selectedDate, filterTime, timeIntervals) : []),
    [selectedDate, filterTime, timeIntervals]
  );

  const handleDateChipClick = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeChipClick = (time) => {
    setSelectedTime(time);
    onChange(time);
  };

  const handleModalDateChange = (date) => {
    const d = new Date(date);
    setSelectedDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    setSelectedTime(d);
    onChange(d);
  };

  return (
    <div>
      <DateChips
        dates={visibleDates}
        selectedDate={selectedDate}
        onSelectDate={handleDateChipClick}
        onOtherClick={() => setShowModal(true)}
      />
      {selectedDate && (
        <TimeChips
          timeSlots={timeSlots}
          selectedTime={selectedTime}
          onSelectTime={handleTimeChipClick}
        />
      )}
      {timezoneLabel && <TimezoneLabel>{timezoneLabel}</TimezoneLabel>}
      <OtherDateModal
        open={showModal}
        onClose={() => setShowModal(false)}
        selected={selectedTime}
        onChange={handleModalDateChange}
        filterDate={filterDate}
        filterTime={filterTime}
        timeIntervals={timeIntervals}
        minDate={minDate}
      />
    </div>
  );
};

export default DateTimeSlotPicker;
